import { useState, useCallback } from 'react'
import { Upload, X, Loader2, ImageIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

interface ImageUploadProps {
  value: string | null | undefined
  onChange: (url: string | null) => void
  accountId: string
  disabled?: boolean
}

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp']

export function ImageUpload({
  value,
  onChange,
  accountId,
  disabled = false,
}: ImageUploadProps) {
  const { toast } = useToast()
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const uploadImage = useCallback(
    async (file: File) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast({ title: 'Error', description: 'Invalid file type. Please use PNG, JPG, or WebP.', variant: 'destructive' })
        return
      }

      if (file.size > MAX_FILE_SIZE) {
        toast({ title: 'Error', description: 'File too large. Maximum size is 5MB.', variant: 'destructive' })
        return
      }

      setIsUploading(true)

      try {
        const fileExt = file.name.split('.').pop()
        const fileName = `${accountId}/${Date.now()}.${fileExt}`

        const { error: uploadError } = await supabase.storage
          .from('vehicle-images')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false,
          })

        if (uploadError) throw uploadError

        const {
          data: { publicUrl },
        } = supabase.storage.from('vehicle-images').getPublicUrl(fileName)

        onChange(publicUrl)
        toast({ title: 'Image uploaded successfully' })
      } catch (error) {
        console.error('Upload error:', error)
        toast({ title: 'Error', description: 'Failed to upload image', variant: 'destructive' })
      } finally {
        setIsUploading(false)
      }
    },
    [accountId, onChange, toast]
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        uploadImage(file)
      }
    },
    [uploadImage]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragging(false)

      const file = e.dataTransfer.files?.[0]
      if (file) {
        uploadImage(file)
      }
    },
    [uploadImage]
  )

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const removeImage = useCallback(async () => {
    if (!value) return

    try {
      // Extract the file path from the URL
      const url = new URL(value)
      const pathParts = url.pathname.split('/storage/v1/object/public/vehicle-images/')
      if (pathParts[1]) {
        await supabase.storage.from('vehicle-images').remove([pathParts[1]])
      }
    } catch (error) {
      console.error('Error removing image:', error)
    }

    onChange(null)
  }, [value, onChange])

  if (value) {
    return (
      <div className="relative">
        <div className="relative aspect-video w-full overflow-hidden rounded-lg border bg-muted">
          <img
            src={value}
            alt="Vehicle"
            className="h-full w-full object-cover"
          />
        </div>
        <Button
          type="button"
          variant="destructive"
          size="icon"
          className="absolute -right-2 -top-2 h-8 w-8"
          onClick={removeImage}
          disabled={disabled}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'relative flex aspect-video w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors',
        isDragging
          ? 'border-primary bg-primary/5'
          : 'border-muted-foreground/25 hover:border-muted-foreground/50',
        disabled && 'cursor-not-allowed opacity-50'
      )}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <input
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        onChange={handleFileChange}
        disabled={disabled || isUploading}
        className="absolute inset-0 cursor-pointer opacity-0"
      />
      {isUploading ? (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">Uploading...</p>
        </>
      ) : (
        <>
          {isDragging ? (
            <Upload className="h-10 w-10 text-primary" />
          ) : (
            <ImageIcon className="h-10 w-10 text-muted-foreground" />
          )}
          <p className="mt-2 text-sm text-muted-foreground">
            {isDragging ? 'Drop image here' : 'Drag & drop an image'}
          </p>
          <p className="text-xs text-muted-foreground">or click to browse</p>
          <p className="mt-2 text-xs text-muted-foreground/75">
            PNG, JPG, WebP up to 5MB
          </p>
        </>
      )}
    </div>
  )
}
